import { Tabs, v2UiTabOverrideColors } from '@cibo/ui'
import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import { Box, Breadcrumbs, Link, Skeleton, Stack, Tab, useTheme } from '@mui/material'
import { Fragment, ReactElement } from 'react'
import { Link as RRLink, LinkProps as RRLinkProps, To } from 'react-router-dom'
import { SaveIndicator } from '../../consts/Theme'

type SlotType =
  | (NavTabsProps & {
      type: 'tabs'
    })
  | {
      type: 'saveIndicator'
    }
  | (NavButtonProps & {
      type: 'button'
    })
  | {
      type: 'other'
      component: ReactElement
    }

interface NavTabsProps {
  tabValue?: string
  defaultTabValue: string
  tabs: TabShape[]
}

interface NavButtonProps extends LoadingButtonProps {
  name: string
  queryLoading?: boolean
  width?: number
}

export interface BreadcrumbShape extends Omit<RRLinkProps, 'to'> {
  to?: To
  isLoading?: boolean
  name: string
}

export interface TabShape {
  to: string
  label: string
  dataTestid?: string
  value: string
}

interface BreadcrumbNavProps {
  breadcrumbs: BreadcrumbShape[]
  slots?: [SlotType, SlotType] | [SlotType]
}

export const BreadcrumbNavigation = ({ breadcrumbs, slots }: BreadcrumbNavProps) => {
  const { palette } = useTheme()
  return (
    <Stack justifyContent="space-between" direction="row" alignItems="center" pr={3}>
      <Breadcrumbs
        sx={{
          p: 3,
          color: 'text.secondary',
          '.MuiBreadcrumbs-li:last-child': { color: 'text.primary' },
        }}
      >
        {breadcrumbs.map(({ isLoading, name, to, ...linkProps }, index) => {
          if (isLoading) {
            return <Skeleton key={index} width="140px" />
          }
          if (!!to) {
            return (
              <Link
                key={name}
                component={RRLink}
                color="inherit"
                underline="hover"
                {...linkProps}
                to={to}
              >
                {name}
              </Link>
            )
          } else {
            return (
              <Link key={name} color="inherit" underline="none" {...linkProps}>
                {name}
              </Link>
            )
          }
        })}
      </Breadcrumbs>

      {!!slots?.length && (
        <Stack direction="row" spacing={2} alignItems="center">
          {slots.map((slot, index) => {
            if (slot.type === 'tabs') {
              const slotProps = slot as NavTabsProps
              return (
                <Box key={index} py={2}>
                  <Tabs
                    shadow={false}
                    value={slotProps.tabValue || slotProps.defaultTabValue}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColorOverrides={v2UiTabOverrideColors(palette)}
                  >
                    {slotProps.tabs.map(tab => (
                      <Tab key={tab.value} {...tab} role="link" component={RRLink} />
                    ))}
                  </Tabs>
                </Box>
              )
            }
            if (slot.type === 'button') {
              const {
                queryLoading,
                name,
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                type,
                style,
                width,
                ...loadingButtonProps
              } = slot as NavButtonProps
              return queryLoading ? (
                <Skeleton
                  key={name}
                  variant="rounded"
                  style={{ ...style, marginLeft: '24px' }}
                  height="36px"
                  width={width || '180px'}
                />
              ) : (
                <LoadingButton
                  key={name}
                  disableElevation
                  style={{ ...style, marginLeft: '24px' }}
                  variant="contained"
                  color="primary"
                  {...loadingButtonProps}
                >
                  {name}
                </LoadingButton>
              )
            }
            if (slot.type === 'saveIndicator') {
              return <SaveIndicator key={index} />
            }
            return <Fragment key={index}>{slot.component}</Fragment>
          })}
        </Stack>
      )}
    </Stack>
  )
}
